import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

// Format validation errors into readable strings
const formatValidationErrors = (errors) => {
    if (!Array.isArray(errors)) return ['Validation error occurred'];
    
    return errors.map(error => {
        const value = error.value || 'N/A';
        const message = error.msg || 'Invalid value';
        return `The value "${value}" is invalid: ${message}`;
    });
};

export const usePurchaseMutation = (applicationFormik, {
    setUploadComplete,
    setKey,
    setPaymentInProgress,
    setFormStage,
    setPaymentRef,
    setErrorMessage
}) => {
    return useMutation({
        mutationFn: async (paymentData) => {
            const formData = new FormData();

            // Append application form values
            Object.keys(paymentData).forEach(key => {
                if (key !== 'files') { // Skip files for now
                    formData.append(key, paymentData[key]);
                }
            });

            // Append files
            if (applicationFormik.values.files.length > 0) {
                applicationFormik.values.files.forEach(file => {
                    formData.append('files', file);
                });
            }

            const response = await fetch(`${import.meta.env.VITE_API_URL}/purchase`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                },
                body: formData,
                mode: 'cors',
            });

            const data = await response.json();
            if (response.status !== 200) {
                if(data.errorCode === 'ERR_VALIDATION') {
                    setErrorMessage(formatValidationErrors(data.errors));
                }
                throw new Error(data.errorCode);
            }
            return data;
        },
        onSuccess: (data) => {
            setKey(data.key);
            setPaymentInProgress(false);
            setFormStage('success');
            setPaymentRef(data.transactionId);
            window.scrollTo(0, 0);
        },
        onError: (error) => {
            console.error('Error making API call:', error);
            if(error.message === 'ERR_VALIDATION') {
                setFormStage('validation_error');
            }
            else if (error.message === 'ERR_EWAY_PURCHASE' || error.errorCode === 'ERR_EWAY_PURCHASE') {
                setFormStage('failed_purchase');
            } else if (error.message === 'ERR_PURCHASE_DOCUMENT' || error.errorCode === 'ERR_PURCHASE_DOCUMENT') {
                setFormStage('failed_purchase_document');
            } else {
                setFormStage('failed');
            }
            setPaymentInProgress(false);
        }
    });
};

export const useAdminPurchaseMutation = ({
    isAuthenticated,
    user,
    applicationFormik,
    setUploadComplete,
    setKey,
    setPaymentInProgress,
    setFormStage,
    setAdminError
}) => {
    return useMutation({
        mutationFn: async (values) => {
            if (!isAuthenticated) return;
            const formData = new FormData();

            // Append application form values
            Object.keys(values).forEach(key => {
                formData.append(key, values[key]);
            });

            // Append files
            if (applicationFormik.values.files.length > 0) {
                applicationFormik.values.files.forEach(file => {
                    formData.append('files', file);
                });
            }
            
            const response = await fetch(`${import.meta.env.VITE_API_URL}/admin-purchase`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${user.bearer}`,
                    'Accept': 'application/json',
                },
                body: formData,
                mode: 'cors',
            });
            const data = await response.json();
            if (response.status !== 200) {
                if(data.errorCode === 'ERR_VALIDATION') {
                    setAdminError(formatValidationErrors(data.errors));
                }
                else {
                    setAdminError(data.message);
                }
                throw new Error(data.errorCode);
            }
            return data;
        },
        onSuccess: (data) => {
            setKey(data.key);
            setFormStage('success');
        },
        onError: (error) => {
            console.error('Error making API call:', error);
            if(error.code === 'ERR_VALIDATION') {
                setFormStage('validation_error');
            }
            else if(error.errorCode === 'INVALID_TOKEN' || error.errorCode === 'TOKEN_VALIDATION_ERROR') {
                setFormStage('failed_auth');
            } else {
                setFormStage('failed');
            }
        }
    });
};

